/**
 * 보호자 활동 로그 (Guardian Activity Log) API 서비스
 *
 * 백엔드 명세: BACKEND_HANDOFF_ACTIVITY_LOGS.md
 *  - GET /api/admin/activity-logs/guardian-options
 *  - GET /api/admin/activity-logs/guardian
 */

import { apiClient } from './client';
import type { TicketProductType } from './tickets';

// ─── 분류 enum ────────────────────────────────────────────

/** 보호자 로그 상위 카테고리 */
export type GuardianLogCategory =
  | 'PAYMENT'   // 결제 (이용권 구매, 발급)
  | 'TICKET'    // 이용권 (사용, 회수)
  | 'NOTICE';   // 알림장 (수신, 발신)

/** 세부 이벤트 코드 */
export type GuardianLogEventCode =
  | 'TICKET_PURCHASE'   // 이용권 구매 (보호자가 직접 결제 구매)
  | 'TICKET_GRANTED'    // 이용권 발급 (사업자가 발급)
  | 'TICKET_USE'        // 이용권 사용
  | 'TICKET_REVOKE'     // 이용권 회수
  | 'NOTICE_RECEIVE'    // 알림장 수신 (사업자 발신 → 보호자 수신)
  | 'NOTICE_SEND';      // 알림장 발신 (보호자 발신 → 사업자 수신)

export const GUARDIAN_LOG_CATEGORY_LABEL: Record<GuardianLogCategory, string> = {
  PAYMENT: '결제',
  TICKET: '이용권',
  NOTICE: '알림장',
};

export const GUARDIAN_EVENT_TO_CATEGORY: Record<
  GuardianLogEventCode,
  GuardianLogCategory
> = {
  TICKET_PURCHASE: 'PAYMENT',
  TICKET_GRANTED: 'PAYMENT',
  TICKET_USE: 'TICKET',
  TICKET_REVOKE: 'TICKET',
  NOTICE_RECEIVE: 'NOTICE',
  NOTICE_SEND: 'NOTICE',
};

// ─── 이벤트별 payload (discriminated union) ────────────────

interface GuardianLogBase {
  logSeq: number;
  /** 로그 일시 (ISO 'YYYY-MM-DDTHH:mm:ss') */
  dtLog: string;
  /** 작업 수행자 (보호자 계정) */
  userSeq: number;
  userName: string;
}

export type GuardianActivityLog = GuardianLogBase &
  (
    | {
        eventCode: 'TICKET_PURCHASE';
        payload: {
          petName: string;
          ticketName: string;
          productType: TicketProductType;
          /** 이용권 1매의 사용 가능 횟수 (= HIST_PURCHASE_TICKET.SPLIT_COUNT) */
          quantity: number;
          /** 구매한 매수. BE 가 미전송 시 기본값 1. */
          purchaseQty?: number;
          price: number;
        };
      }
    | {
        eventCode: 'TICKET_GRANTED';
        payload: {
          petName: string;
          ticketName: string;
          productType: TicketProductType;
          companyName: string;
          /** 이용권 1매의 사용 가능 횟수 */
          quantity: number;
          /** 발급된 매수. BE 가 미전송 시 기본값 1. */
          purchaseQty?: number;
          price: number;
        };
      }
    | {
        eventCode: 'TICKET_USE';
        payload: {
          /** 예약일시 */
          dtReservation: string;
          petName: string;
          /** 예약요청일 */
          dtRequested: string;
          productType: TicketProductType;
          companyName: string;
        };
      }
    | {
        eventCode: 'TICKET_REVOKE';
        payload: {
          /** 취소일시 */
          dtCanceled: string;
          petName: string;
          productType: TicketProductType;
          companyName: string;
        };
      }
    | {
        eventCode: 'NOTICE_RECEIVE';
        payload: {
          fromCompanyName: string;
          petName: string;
        };
      }
    | {
        eventCode: 'NOTICE_SEND';
        payload: {
          toCompanyName: string;
          petName: string;
        };
      }
  );

// ─── 검색 파라미터 / 페이지 응답 ───────────────────────────

export interface GuardianActivityLogSearchRequest {
  /** 보호자 시퀀스 — 없으면 전체 */
  userSeq?: number;
  dateFrom?: string;
  dateTo?: string;
  categories?: GuardianLogCategory[];
  page?: number;
  size?: number;
}

export interface GuardianActivityLogPage {
  content: GuardianActivityLog[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface GuardianOption {
  userSeq: number;
  userName: string;
  /** 콤보 표시 보조용 — 전화번호 뒷자리 4자리 */
  phoneTail: string;
}

// ─── 응답 언랩 / 파라미터 정리 헬퍼 ───────────────────────

/**
 * BE 응답 `{ data: T }` 형태(또는 ServiceResult) 언랩.
 * dailyReservations.ts / tickets.ts 와 동일한 패턴.
 */
function unwrap<T>(resp: { data?: unknown }): T {
  const raw = resp.data as { data?: T } | T | undefined;
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>)
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function compactParams<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  (Object.keys(obj) as (keyof T)[]).forEach(k => {
    const v = obj[k];
    if (v === undefined || v === null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    if (Array.isArray(v) && v.length === 0) return;
    out[k] = v;
  });
  return out;
}

// ─── API 서비스 ───────────────────────────────────────────

const BASE = '/admin/activity-logs';

export const guardianActivityLogsApi = {
  /**
   * GET /api/admin/activity-logs/guardian-options
   * - keyword: 이름/전화번호 뒷자리 부분 검색 (자동완성용)
   * - size: 기본 50, 최대 200 (BE 측 정책)
   */
  getGuardianOptions: async (keyword?: string): Promise<GuardianOption[]> => {
    const response = await apiClient.get(`${BASE}/guardian-options`, {
      params: { keyword, size: 50 },
    });
    return unwrap<GuardianOption[]>(response);
  },

  search: async (
    req: GuardianActivityLogSearchRequest = {},
  ): Promise<GuardianActivityLogPage> => {
    const response = await apiClient.get(`${BASE}/guardian`, {
      params: compactParams({
        userSeq: req.userSeq,
        dateFrom: req.dateFrom,
        dateTo: req.dateTo,
        categories: req.categories,
        page: req.page ?? 0,
        size: req.size ?? 20,
      }),
    });
    return unwrap<GuardianActivityLogPage>(response);
  },
};
